import { Link } from 'react-router-dom'

export function Header({ showInfo, onToggleInfo, onOpenFeedback, onToggleTheme, theme }) {
  return (
    <header className="mb-8">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-white">
          <svg className="w-7 h-7 shrink-0" viewBox="0 0 32 32" aria-hidden="true">
            <g stroke="currentColor" strokeWidth="2">
              <circle cx="5" cy="16" r="3.5" className="fill-white dark:fill-neutral-900"/>
              <circle cx="16" cy="6" r="3.5" className="fill-white dark:fill-neutral-900"/>
              <circle cx="16" cy="16" r="3.5" className="fill-white dark:fill-neutral-900"/>
              <circle cx="16" cy="26" r="3.5" className="fill-white dark:fill-neutral-900"/>
              <circle cx="27" cy="16" r="3.5" className="fill-white dark:fill-neutral-900"/>
            </g>
          </svg>
          <span>a<span className="text-blue-600 dark:text-blue-400">risk</span></span>
        </h1>
        <div className="flex items-center gap-5">
          <Link
            to="/about"
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
          >
            About
          </Link>
          <Link
            to="/faq"
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
          >
            FAQ
          </Link>
          <Link
            to="/blog"
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
          >
            Blog
          </Link>
          <button
            onClick={onOpenFeedback}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
          >
            Feedback
          </button>
          <button
            onClick={onToggleTheme}
            className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? (
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="mt-8 text-center">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white leading-snug">
          Privacy infrastructure for the agentic AI era
        </h2>
        <p className="mt-3 text-sm sm:text-base text-gray-500 dark:text-neutral-400 max-w-xl mx-auto leading-relaxed">
          Anonymise sensitive data before it reaches any AI model
        </p>
      </div>

      {/* Trust badges */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-300">
          <svg className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          100% client-side
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-300">
          <svg className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.36 6.64A9 9 0 005.64 18.36M5.64 5.64A9 9 0 0018.36 18.36M1 1l22 22" />
          </svg>
          Zero data transmission
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-300">
          <svg className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
          </svg>
          No account required
        </div>
      </div>

      {/* Learn more toggle */}
      <div className="mt-6 text-center">
        <button
          onClick={onToggleInfo}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors inline-flex items-center gap-1"
        >
          Learn more
          <svg
            className={`w-3.5 h-3.5 transition-transform ${showInfo ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {showInfo && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-neutral-700 space-y-8">
          {/* How it works */}
          <div>
            <h3 className="text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wide mb-3">How it works</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm mb-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs flex items-center justify-center font-medium">1</span>
                <span className="text-gray-600 dark:text-neutral-300">Paste, type, or open PDF</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs flex items-center justify-center font-medium">2</span>
                <span className="text-gray-600 dark:text-neutral-300">Double-click words to redact</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs flex items-center justify-center font-medium">3</span>
                <span className="text-gray-600 dark:text-neutral-300">Copy & share with AI</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs flex items-center justify-center font-medium">4</span>
                <span className="text-gray-600 dark:text-neutral-300">Paste the AI response to restore</span>
              </div>
            </div>
            <div className="text-center text-xs text-gray-400 dark:text-neutral-500">
              <span className="text-gray-500 dark:text-neutral-400">"Email john@acme.com"</span>
              <span className="mx-2">&rarr;</span>
              <span className="text-gray-500 dark:text-neutral-400">"Email <code className="text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1 rounded">xxxxx0001</code>@<code className="text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1 rounded">xxxxx0002</code>.com"</span>
            </div>
          </div>

          {/* Why protect */}
          <div className="pt-6 border-t border-gray-100 dark:border-neutral-800">
            <h3 className="text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Why it matters</h3>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div className="text-center p-3 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-2xl font-bold text-red-500 dark:text-red-400">48%</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 mt-1">admit entering non-public company data into AI</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-2xl font-bold text-orange-500 dark:text-orange-400">63%</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 mt-1">of organisations have no AI governance policies</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">40%</div>
                <div className="text-xs text-gray-500 dark:text-neutral-400 mt-1">of files uploaded to AI contain personal data</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[10px] text-gray-400 dark:text-neutral-500 mb-4">
              <a href="https://www.cisco.com/c/en/us/about/trust-center/data-privacy-benchmark-study.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-neutral-300 underline decoration-dotted underline-offset-2">Cisco, 2024</a>
              <a href="https://www.ibm.com/reports/data-breach" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-neutral-300 underline decoration-dotted underline-offset-2">IBM, 2025</a>
              <a href="https://go.layerxsecurity.com/the-layerx-enterprise-ai-saas-data-security-report-2025" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-neutral-300 underline decoration-dotted underline-offset-2">LayerX, 2025</a>
            </div>
            <div className="text-sm text-gray-600 dark:text-neutral-400 space-y-2">
              <p>It happens to all of us, and for good reason:</p>
              <ul className="space-y-2 text-gray-500 dark:text-neutral-400">
                <li>&#8226; <strong className="text-gray-700 dark:text-neutral-300">Feels private.</strong> Chat interfaces feel anonymous, but they're not</li>
                <li>&#8226; <strong className="text-gray-700 dark:text-neutral-300">Easy to miss.</strong> Sensitive details hide in plain sight: names, dates, amounts buried in longer text</li>
                <li>&#8226; <strong className="text-gray-700 dark:text-neutral-300">Productivity pressure.</strong> The focus is on getting things done</li>
              </ul>
              <p className="pt-2 text-gray-700 dark:text-neutral-300 font-medium">
                We make it easy to stay in control.
              </p>
            </div>
          </div>

          {/* Your data stays on your device */}
          <div className="pt-6 border-t border-gray-100 dark:border-neutral-800">
            <h3 className="text-xs font-medium text-gray-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Your data never leaves this page</h3>
            <ul className="space-y-2 text-sm text-gray-500 dark:text-neutral-400">
              <li>&#8226; <strong className="text-gray-700 dark:text-neutral-300">No servers.</strong> Everything runs entirely in your browser</li>
              <li>&#8226; <strong className="text-gray-700 dark:text-neutral-300">No transmission.</strong> Your text is never sent or stored anywhere</li>
              <li>&#8226; <strong className="text-gray-700 dark:text-neutral-300">No account required.</strong> Nothing to sign up for, nothing to track you</li>
            </ul>
            <p className="mt-3 text-sm text-gray-600 dark:text-neutral-400 italic">
              Prove it yourself: open DevTools &rarr; Network tab while using arisk.ai. You'll see no requests containing your text.
            </p>
          </div>

        </div>
      )}

      {/* Feedback CTA */}
      <p className={`mt-6 text-center text-sm text-gray-400 dark:text-neutral-500 ${showInfo ? 'pt-5 border-t border-gray-200 dark:border-neutral-700' : ''}`}>
        Questions, feedback, or feature ideas?{' '}
        <button
          onClick={onOpenFeedback}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Let us know.
        </button>
      </p>
    </header>
  )
}
